import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import dayjs from "dayjs";
import { useSolutionsStore } from "@/store/solutions.store";
import { useAzureStore } from "@/store/azure.store";
import {
  getCallbackUrl,
  getApiClientUrl,
  getOldAppClientId,
  getNewAppClientId,
} from "@/config/runtimeConfig";
import { useFlag } from "@/composables/useFlags/useFlag";
import { $http } from "@/plugins/api";
import {
  PHISH_SERVICE,
  SAT_SERVICE,
  PHISHING_SIMULATION,
} from "@/utils/services";
import { useToastStore } from "@/components/TToasts/toast.store";
import { useI18n } from "vue-i18n";
import {
  readConsentResult,
  clearConsentResult,
  type ConsentResult,
} from "@/utils/consent";

interface OnboardingStatusEntry {
  status: boolean;
  created_at: string;
  updated_at: string;
}

interface OnboardingStatusResponse {
  onboarding: OnboardingStatusEntry;
  journal_rule: OnboardingStatusEntry;
  ndr_address: OnboardingStatusEntry;
}

export function useConnectionsState() {
  const { t } = useI18n();
  const authStore = useAuthenticationStore();
  const toastStore = useToastStore();
  const azureStore = useAzureStore();
  const { isOnLegacyApp, isOnNewApp, connectedAzureApp } =
    storeToRefs(azureStore);
  // The whole Azure app-migration surface on this page (new-app connect, the
  // permissions-update banner, the connection-status detection, and the
  // NDR/journal new-app gate) sits behind M365_AZURE_APP_MIGRATION. With the
  // flag off the page behaves exactly as it did before P-287: a plain old-app
  // connect and phish-service-only NDR/journal visibility. The separate
  // M365_AZURE_APP_CHOOSER flag still governs the in-card app chooser.
  const { isFeatureAvailable: isAppMigrationEnabled } = useFlag(
    "M365_AZURE_APP_MIGRATION",
  );

  const isRequestPending = ref(false);
  const pendingM365App = ref<"old" | "new" | null>(null);
  const m365UpdateStatus = ref<"idle" | "success" | "error">("idle");
  const isNdrRequestPending = ref(false);
  const isJournalRuleRequestPending = ref(false);
  const receivedMessage = ref();
  const timestamp = ref<string | null>(null);
  const callback = getCallbackUrl();
  const onboardingStatus = ref<OnboardingStatusResponse | null>(null);
  const ndrError = ref(false);
  const ndrErrorMessage = ref("");
  const journalRuleError = ref(false);
  const journalRuleErrorMessage = ref("");
  const initialLoadComplete = ref(false);
  const ndrConsentHandled = ref(false);
  const journalRuleConsentHandled = ref(false);

  const selectedCustomer = computed(() => authStore.selectedCustomer);
  const selectedAccount = computed(() => authStore.selectedAccount);
  const isM365Connected = computed(
    () => onboardingStatus.value?.onboarding.status ?? false,
  );
  const isNdrConfigured = computed(
    () => onboardingStatus.value?.ndr_address.status ?? false,
  );
  const isJournalRuleConfigured = computed(
    () => onboardingStatus.value?.journal_rule.status ?? false,
  );
  // Production has no VITE_CLIENT_ID_NEW yet (the prod Azure app is not
  // registered), so getNewAppClientId() is empty there. When that happens the
  // primary connect must fall back to the working old app instead of building a
  // consent URL with client_id=undefined; it auto-switches to the new app as
  // soon as the id is configured.
  const isNewAppConfigured = computed(() => !!getNewAppClientId());
  const primaryConnectApp = computed<"old" | "new">(() =>
    isAppMigrationEnabled.value && isNewAppConfigured.value ? "new" : "old",
  );
  // The migration ("Update permissions") banner only makes sense when there is
  // a new app to migrate to; suppress it entirely while the new app id is absent
  // or the migration flag is off.
  const needsAppUpdate = computed(
    () =>
      isAppMigrationEnabled.value &&
      isOnLegacyApp.value &&
      isNewAppConfigured.value,
  );

  const { solutions } = storeToRefs(useSolutionsStore());
  const isPhishServiceActive = computed(() =>
    solutions.value.some(
      (s: { solution_uuid: string }) => s.solution_uuid === PHISH_SERVICE,
    ),
  );
  // NDR and journal-rule setup require the new Azure app's permissions, so the
  // tiles and stepper steps stay hidden until the customer has updated their
  // permissions (i.e. connected to the new app). Being on the legacy app or
  // not connected at all both keep them hidden.
  const canShowNdrAndJournal = computed(
    () =>
      isPhishServiceActive.value &&
      (isAppMigrationEnabled.value ? isOnNewApp.value : true),
  );
  const isSatServiceActive = computed(() =>
    solutions.value.some(
      (s: { solution_uuid: string }) => s.solution_uuid === SAT_SERVICE,
    ),
  );
  const isPhsServiceActive = computed(() =>
    solutions.value.some(
      (s: { solution_uuid: string }) => s.solution_uuid === PHISHING_SIMULATION,
    ),
  );

  const formattedM365LastUpdate = computed(() => {
    const updatedAt = onboardingStatus.value?.onboarding.updated_at;
    if (!updatedAt) return "";
    return dayjs(updatedAt).format("YYYY/MM/DD, HH:mm");
  });

  const formattedNdrLastUpdate = computed(() => {
    const updatedAt = onboardingStatus.value?.ndr_address.updated_at;
    if (!updatedAt) return "";
    return dayjs(updatedAt).format("YYYY/MM/DD, HH:mm");
  });

  const formattedJournalRuleLastUpdate = computed(() => {
    const updatedAt = onboardingStatus.value?.journal_rule.updated_at;
    if (!updatedAt) return "";
    return dayjs(updatedAt).format("YYYY/MM/DD, HH:mm");
  });

  const channel = new BroadcastChannel("azure-connection");
  const ndrChannel = new BroadcastChannel("ndr-connection");

  watch(
    () => receivedMessage.value,
    async (newValue) => {
      const uuid = newValue;
      if (uuid && uuid.value?.data != null) {
        await azureStore.getAllAzureAccounts(uuid);
      }
    },
  );

  // Opens the M365 admin-consent popup for the given Azure app and, once the
  // popup closes, refreshes the connection state. `onClosed` lets a caller
  // (e.g. the permissions-update flow) react to the outcome instead of the
  // default "connected" success toast.
  const openM365Consent = (
    app: "old" | "new",
    onClosed?: (result: ConsentResult | null) => void,
  ) => {
    isRequestPending.value = true;
    pendingM365App.value = app;
    // Drop any stale consent result so a previous attempt cannot leak into this
    // one (the popup persists a fresh result to the same key on completion).
    clearConsentResult();

    const name = btoa(
      selectedCustomer.value?.name || selectedAccount.value?.name || "",
    );
    const uuid =
      selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "";
    const billingId = selectedCustomer.value?.billing_system_id;

    const clientId = app === "old" ? getOldAppClientId() : getNewAppClientId();

    const state = {
      v: billingId,
      o: uuid,
      n: name,
      c: clientId,
      t: timestamp.value,
    };

    const encodeObj = encodeURI(JSON.stringify(state));
    const strWindowFeatures =
      "location=yes,height=570,width=520,scrollbars=yes,status=yes";

    const URL = `https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=${clientId}&redirect_uri=${callback}&state=${encodeObj}&scope=https://outlook.office365.com/.default`;

    const popupWindow = window.open(URL, "_blank", strWindowFeatures);

    const checkIfPopupWindowClosed = setInterval(async () => {
      if (popupWindow?.closed) {
        clearInterval(checkIfPopupWindowClosed);
        isRequestPending.value = false;
        pendingM365App.value = null;
        await azureStore.getAllAzureAccounts(uuid);
        // Refresh the connection status too: it is what drives both the
        // per-app card state (old/new) and the page-level SFI migration
        // banner. Without this the banner keeps nagging after a migration.
        // Gated with the rest of the migration surface behind the flag.
        if (isAppMigrationEnabled.value) {
          await azureStore.getConnectionStatus(uuid);
        }
        await fetchOnboardingStatus(uuid);
        // The consent popup persists its outcome (success or failure) from the
        // redirect URL. This is the only reliable signal that admin consent
        // actually completed: closing/cancelling the popup leaves no result, so
        // we must not infer success from the connection state alone.
        const consentResult = readConsentResult();
        clearConsentResult();
        if (onClosed) {
          onClosed(consentResult);
        } else if (consentResult?.success && isM365Connected.value) {
          toastStore.displayToast({
            title: t("success"),
            message: t("microsoft-365-connected"),
            duration: 5000,
            variant: "success",
            testId: "m365-connection-toast-success",
          });
        }
      }
    }, 500);
  };

  const onConnectM365 = (useOldApp = false) => {
    // The primary connect action targets the new Azure app whenever it is
    // configured; the legacy app is otherwise reachable through the explicit
    // chooser button (gated behind M365_AZURE_APP_CHOOSER, dev/test only). When
    // the new app id is absent (prod, until the app is registered) the primary
    // button falls back to the old app so it keeps working. Both old-app paths
    // are plain connects — no migration banner.
    if (useOldApp || primaryConnectApp.value === "old") {
      openM365Consent("old");
      return;
    }
    // Connecting to the new app mirrors the permissions-update flow: surface the
    // same in-card success/error banner instead of the default "connected"
    // toast. Green shows only when the consent redirect reported success —
    // closing the popup (no result) or an explicit failure shows the error
    // banner, even for an account that was never connected to the old app.
    m365UpdateStatus.value = "idle";
    openM365Consent("new", (result) => {
      m365UpdateStatus.value = result?.success ? "success" : "error";
    });
  };

  const onConnectOldAppM365 = () => onConnectM365(true);

  // The update action always targets the new Azure app (it is the whole point
  // of the migration) regardless of the chooser flag. Success/error is derived
  // from the consent redirect outcome, not the connection state: cancelling the
  // popup while already on the new app must not be read as a fresh success.
  const onUpdateM365Permissions = () => {
    m365UpdateStatus.value = "idle";
    openM365Consent("new", (result) => {
      m365UpdateStatus.value = result?.success ? "success" : "error";
    });
  };

  onMounted(async () => {
    try {
      isRequestPending.value = true;
      const uuid =
        selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "";
      const now = new Date();
      timestamp.value = now.toISOString().replace("T", " ").split(".")[0];
      await azureStore.getAllAzureAccounts(uuid);
      // Needed so the card knows which Azure app the customer is on (old/new)
      // and can show the permissions-update banner and its buttons. It is
      // isolated in its own try/catch: connection-status is best-effort, and a
      // failure here must not hide the connected state (onboarding status) or
      // block initialLoadComplete. Gated behind the migration flag — before
      // P-287 the page never fetched connection-status here.
      if (isAppMigrationEnabled.value) {
        try {
          await azureStore.getConnectionStatus(uuid);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }

      await fetchOnboardingStatus(uuid);
      initialLoadComplete.value = true;
    } catch (e) {
      /* eslint-disable no-console */
      console.log(e);
    } finally {
      isRequestPending.value = false;
    }

    channel.onmessage = (event) => {
      receivedMessage.value = event.data;
    };

    ndrChannel.onmessage = async (event) => {
      const msg = event.data;
      if (isJournalRuleRequestPending.value) {
        if (msg.success) {
          if (journalRuleConsentHandled.value) return;
          journalRuleConsentHandled.value = true;
          journalRuleError.value = false;
          journalRuleErrorMessage.value = "";
          await onConfigureJournalRule();
        } else {
          journalRuleError.value = true;
          journalRuleErrorMessage.value =
            msg.errorDescription || msg.error || "";
          isJournalRuleRequestPending.value = false;
        }
      } else {
        if (msg.success) {
          if (ndrConsentHandled.value) return;
          ndrConsentHandled.value = true;
          ndrError.value = false;
          ndrErrorMessage.value = "";
          await onConfigureNdr();
        } else {
          ndrError.value = true;
          ndrErrorMessage.value = msg.errorDescription || msg.error || "";
          isNdrRequestPending.value = false;
        }
      }
    };
  });

  const fetchOnboardingStatus = async (organisationID: string) => {
    const res = await $http.get<OnboardingStatusResponse>(
      `${getApiClientUrl()}/v1/pt/onboarding/${organisationID}/status`,
    );
    onboardingStatus.value = res.data;
  };

  const createJournalRule = (uuid: string) =>
    $http.post<{ url?: string }>(
      `${getApiClientUrl()}/v1/pt/onboarding/${uuid}/journal-rule`,
    );

  const onConfigureJournalRule = async () => {
    const uuid =
      selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "";
    if (!uuid) return;

    // Clear any error from a previous attempt up front (see onConfigureNdr).
    journalRuleError.value = false;
    journalRuleErrorMessage.value = "";
    // Drop any stale consent result so it cannot leak into this attempt.
    clearConsentResult();
    isJournalRuleRequestPending.value = true;
    try {
      const res = await createJournalRule(uuid);
      const consentUrl = res.data?.url;

      if (consentUrl) {
        journalRuleConsentHandled.value = false;
        const strWindowFeatures =
          "location=yes,height=570,width=520,scrollbars=yes,status=yes";
        const popupWindow = window.open(
          consentUrl,
          "_blank",
          strWindowFeatures,
        );

        const checkIfPopupWindowClosed = setInterval(async () => {
          if (!popupWindow?.closed) return;
          clearInterval(checkIfPopupWindowClosed);
          // The consent flow continues via the BroadcastChannel 'success'
          // message (which re-invokes this function). If the window was
          // closed without that message, do NOT re-open it. Re-check the real
          // state instead, in case consent succeeded but the broadcast was
          // lost — this is a read-only status fetch, so it cannot loop.
          if (journalRuleConsentHandled.value) return;
          journalRuleConsentHandled.value = true;
          try {
            const result = readConsentResult();
            if (result && result.success === false) {
              // The consent popup reported a failure. Surface it even when the
              // BroadcastChannel message never reached us (it can be lost when
              // the popup auto-closes right after posting).
              journalRuleError.value = true;
              journalRuleErrorMessage.value =
                result.errorDescription || result.error || "";
            } else {
              await fetchOnboardingStatus(uuid);
            }
          } catch (e) {
            console.error(e);
          } finally {
            clearConsentResult();
            isJournalRuleRequestPending.value = false;
          }
        }, 500);
        return;
      }

      await fetchOnboardingStatus(uuid);
      toastStore.displayToast({
        title: t("success"),
        message: t("journaling-rule-created"),
        duration: 5000,
        variant: "success",
        testId: "journaling-rule-connection-toast-success",
      });
      isJournalRuleRequestPending.value = false;
    } catch (e) {
      console.error(e);
      journalRuleError.value = true;
      journalRuleErrorMessage.value = e instanceof Error ? e.message : "";
      isJournalRuleRequestPending.value = false;
    }
  };

  const updateNdr = (uuid: string) =>
    $http.put<{ url?: string }>(
      `${getApiClientUrl()}/v1/pt/onboarding/${uuid}/ndr-address`,
    );

  const onConfigureNdr = async () => {
    const uuid =
      selectedCustomer.value?.uuid || selectedAccount.value?.uuid || "";
    if (!uuid) return;

    // Clear any error from a previous attempt up front. The success path below
    // never reset it, so a retry that succeeds directly (no fresh consent
    // popup) left a stale error visible alongside the "Active" status. The
    // catch block re-sets it if this attempt also fails.
    ndrError.value = false;
    ndrErrorMessage.value = "";
    // Drop any stale consent result so it cannot leak into this attempt.
    clearConsentResult();
    isNdrRequestPending.value = true;
    try {
      const res = await updateNdr(uuid);
      const consentUrl = res.data?.url;

      if (consentUrl) {
        ndrConsentHandled.value = false;
        const strWindowFeatures =
          "location=yes,height=570,width=520,scrollbars=yes,status=yes";
        const popupWindow = window.open(
          consentUrl,
          "_blank",
          strWindowFeatures,
        );

        const checkIfPopupWindowClosed = setInterval(async () => {
          if (!popupWindow?.closed) return;
          clearInterval(checkIfPopupWindowClosed);
          // The consent flow continues via the BroadcastChannel 'success'
          // message (which re-invokes this function). If the window was
          // closed without that message, do NOT re-open it. Re-check the real
          // state instead, in case consent succeeded but the broadcast was
          // lost — this is a read-only status fetch, so it cannot loop.
          if (ndrConsentHandled.value) return;
          ndrConsentHandled.value = true;
          try {
            const result = readConsentResult();
            if (result && result.success === false) {
              // The consent popup reported a failure. Surface it even when the
              // BroadcastChannel message never reached us (it can be lost when
              // the popup auto-closes right after posting).
              ndrError.value = true;
              ndrErrorMessage.value =
                result.errorDescription || result.error || "";
            } else {
              await fetchOnboardingStatus(uuid);
            }
          } catch (e) {
            console.error(e);
          } finally {
            clearConsentResult();
            isNdrRequestPending.value = false;
          }
        }, 500);
        return;
      }

      await fetchOnboardingStatus(uuid);
      toastStore.displayToast({
        title: t("success"),
        message: t("ndr-email-address-created"),
        duration: 5000,
        variant: "success",
        testId: "ndr-connection-toast-success",
      });
      isNdrRequestPending.value = false;
    } catch (e) {
      console.error(e);
      ndrError.value = true;
      isNdrRequestPending.value = false;
    }
  };

  onBeforeUnmount(() => {
    channel.close();
    ndrChannel.close();
  });

  return {
    isRequestPending,
    pendingM365App,
    isOnLegacyApp,
    isOnNewApp,
    needsAppUpdate,
    primaryConnectApp,
    canShowNdrAndJournal,
    connectedAzureApp,
    m365UpdateStatus,
    isNdrRequestPending,
    isJournalRuleRequestPending,
    isM365Connected,
    isPhishServiceActive,
    isSatServiceActive,
    isPhsServiceActive,
    formattedM365LastUpdate,
    selectedCustomer,
    onConnectM365,
    onConnectOldAppM365,
    onUpdateM365Permissions,
    onConfigureNdr,
    onConfigureJournalRule,
    ndrError,
    ndrErrorMessage,
    journalRuleError,
    journalRuleErrorMessage,
    formattedJournalRuleLastUpdate,
    onboardingStatus,
    fetchOnboardingStatus,
    isNdrConfigured,
    isJournalRuleConfigured,
    formattedNdrLastUpdate,
    initialLoadComplete,
  };
}
